import React, { useState, useEffect, useCallback } from 'react';
import UserScoreboard from './UserScoreboard';
import BetCreationForm from './BetCreationForm';
import BetList from './BetList';
import BetResponseList from './BetResponseList';
import HistoricalBetCards from './HistoricalBetCards';

function Dashboard({ user }) {
  const [activeBets, setActiveBets] = useState([]);
  const [historicalBets, setHistoricalBets] = useState([]);
  const [pendingResponseBets, setPendingResponseBets] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchUsers = useCallback(async (userEmail) => {
    try {
      const whitelistedEmails = ["dara.mcgann@gmail.com", "tomas.burke@gmail.com", "caoimherooney@gmail.com"];
      const fetchedUsers = await Promise.all(whitelistedEmails.map(async (email) => {
          const userRes = await fetch(process.env.REACT_APP_BACKEND_URL + `/users/${email}`, {
            credentials: 'include',
            headers: {
              'X-User-Email': userEmail
            }
          });
          if (userRes.ok) {
            return userRes.json();
          }
          return null; // Handle cases where user might not be in DB yet
      }));
      setAllUsers(fetchedUsers.filter(u => u !== null));
    } catch (error) {
      console.error("Failed to fetch users for scoreboard:", error);
    }
  }, []);

  const fetchBets = useCallback(async (userEmail) => {
    try {
      const headers = {
        'X-User-Email': userEmail
      };

      const activeRes = await fetch(process.env.REACT_APP_BACKEND_URL + '/bets/active', {
        credentials: 'include',
        headers
      });
      const activeData = await activeRes.json();
      setActiveBets(activeData);

      const historicalRes = await fetch(process.env.REACT_APP_BACKEND_URL + '/bets/history', {
        credentials: 'include',
        headers
      });
      const historicalData = await historicalRes.json();
      setHistoricalBets(historicalData);

      const pendingRes = await fetch(process.env.REACT_APP_BACKEND_URL + '/bets/pending-response', {
        credentials: 'include',
        headers
      });
      const pendingData = await pendingRes.json();
      setPendingResponseBets(pendingData);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    }
  }, []);

  useEffect(() => {
    if (user && user.email) {
      fetchUsers(user.email);
      fetchBets(user.email);
    }
  }, [user, fetchUsers, fetchBets]);

  useEffect(() => {
    // Determine WebSocket URL based on HTTP backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const host = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${host}/ws`;

    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('WebSocket connected');
    };

    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      if (message.event === 'new_bet') {
        const newBet = JSON.parse(message.bet);
        if (newBet.status === 'pending_responses') {
          setPendingResponseBets((prevBets) => [...prevBets, newBet]);
        } else {
          setActiveBets((prevBets) => [...prevBets, newBet]);
        }
        // Re-fetch users to update scoreboard if necessary (e.g., new user joined)
        fetchUsers();
      } else if (message.event === 'bet_resolved') {
        const resolvedBet = JSON.parse(message.bet);
        setActiveBets((prevBets) => prevBets.filter((bet) => bet.id !== resolvedBet.id));
        setHistoricalBets((prevBets) => [...prevBets, resolvedBet]);
        fetchUsers(); // Refresh scoreboard as wins/losses have changed
      } else if (message.event === 'bet_response_submitted') {
        // Refresh pending bets to update the list
        fetchBets();
      } else if (message.event === 'bet_activated') {
        const activatedBet = JSON.parse(message.bet);
        setPendingResponseBets((prevBets) => prevBets.filter((bet) => bet.id !== activatedBet.id));
        setActiveBets((prevBets) => [...prevBets, activatedBet]);
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    return () => {
      ws.close();
    };
  }, [fetchUsers]); // Dependency on fetchUsers to ensure it's up-to-date

  const handleNewBet = (newBet) => {
    // State will be updated via WebSocket broadcast, no need to update directly here.
    // setActiveBets((prevBets) => [...prevBets, newBet]); 
  };

  const handleBetResolved = (resolvedBet) => {
    // State will be updated via WebSocket broadcast, no need to update directly here.
    // setActiveBets((prevBets) => prevBets.filter((bet) => bet.id !== resolvedBet.id));
    // setHistoricalBets((prevBets) => [...prevBets, resolvedBet]);
    // fetchUsers();
  };

  const handleResponseSubmitted = () => {
    // Refresh bets after a response is submitted
    fetchBets();
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ margin: 0 }}>Put a pint on it {user.name}, get a round in!</h1>
      </div>

      {/* User Profile Cards */}
      <UserScoreboard users={allUsers} activeBets={activeBets} />

      {/* Settling on the Counter Section */}
      {pendingResponseBets.length > 0 && (
        <section style={{ marginTop: '40px', marginBottom: '40px' }}>
          <h2>Settling on the Counter</h2>
          <BetResponseList
            bets={pendingResponseBets}
            onResponseSubmitted={handleResponseSubmitted}
            currentUserEmail={user.email}
          />
        </section>
      )}

      {/* Put a Pint On It Button */}
      <div style={{ textAlign: 'center', margin: '40px 0' }}>
        <button
          onClick={() => setIsModalOpen(true)}
          style={{
            padding: '15px 40px',
            fontSize: '20px',
            fontWeight: 'bold',
            cursor: 'pointer',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}
        >
          Put a Pint On It
        </button>
      </div>

      {/* Left in the Keg Section */}
      <section style={{ marginTop: '40px', marginBottom: '40px' }}>
        <h2>Left in the Keg</h2>
        <BetList bets={activeBets} title="" onBetResolved={handleBetResolved} emptyMessage="Line blown, tap a fresh one" currentUserEmail={user.email} />
      </section>

      {/* Bet Creation Modal */}
      <BetCreationForm
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreateBet={handleNewBet}
        whitelistedUsers={allUsers}
        currentUserEmail={user.email}
      />

      {/* Historical Bets */}
      <section style={{ marginBottom: '40px' }}>
        <h2>Rounds Bought</h2>
        <div style={{ border: '1px solid #ccc', padding: '20px', borderRadius: '5px' }}>
          <HistoricalBetCards bets={historicalBets} />
        </div>
      </section>
    </div>
  );
}

export default Dashboard;
